// Rollout telemetry (Karan 09/07): passive error capture + coarse usage events +
// a one-click "Report an issue" button, so we can spot bugs and friction during
// the first weeks without waiting for someone to report them.
//
// Internal staff tool → we log errors, the screen in view, and the action taken.
// NOT the data itself. Every write is best-effort (try/catch) and must NEVER
// break the app — telemetry failing is silent.
import { logTelemetry } from './db.js';

let ctx = { email: null, roles: [], build: null };
let currentScreen = null;
let lastErrorKey = '';   // de-dupe identical errors fired in a tight loop

function write(entry) {
  try {
    logTelemetry({
      ...entry,
      email: ctx.email,
      build: ctx.build,
      screen: currentScreen,
      ua: navigator.userAgent,
      at: new Date().toISOString(),
    }).catch(() => {});
  } catch (e) { /* telemetry must never throw */ }
}

export function setScreen(id) { currentScreen = id; }

// True screenshot via the native screen-capture API (no silent capture exists —
// browsers require this explicit, user-picked share). We grab one frame, shrink
// it and JPEG-encode it small enough to sit inside the telemetry doc (Firestore
// caps docs at ~1 MB). Returns a data URL, or null if cancelled/too big.
async function captureScreenshot() {
  let stream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'browser' }, audio: false, preferCurrentTab: true,
    });
  } catch (e) { return null; } // user cancelled or denied
  try {
    const video = document.createElement('video');
    video.srcObject = stream;
    await video.play();
    await new Promise((r) => setTimeout(r, 250)); // let a frame paint
    const vw = video.videoWidth || 1280, vh = video.videoHeight || 720;
    const scale = Math.min(1, 1280 / vw);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(vw * scale);
    canvas.height = Math.round(vh * scale);
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    let data = canvas.toDataURL('image/jpeg', 0.6);
    if (data.length > 950000) data = canvas.toDataURL('image/jpeg', 0.4);
    return data.length > 950000 ? null : data; // skip rather than exceed the 1 MB doc cap
  } finally {
    stream.getTracks().forEach((t) => t.stop());
  }
}

export function logEvent(name, detail) {
  write({ kind: 'event', name, detail: detail || null });
}

// The last few errors on this page, kept in memory only, so the Issue button
// can attach them (issue-capture.js). Short lines, scrubbed of addresses,
// long tokens and amounts; never written anywhere by themselves.
const RING = [];
const RING_MAX = 6;
let initialised = false;
export function scrubErrorLine(v) {
  return String(v == null ? '' : v).replace(/\s+/g, ' ').trim().slice(0, 240)
    .replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, '[email]')
    .replace(/\b(?:Bearer\s+)?[A-Za-z0-9_\-.]{32,}\b/g, '[redacted]')
    .replace(/[£$€]\s?-?[\d,]+(?:\.\d+)?/g, '[amount]');
}
function remember(source, message) {
  const line = scrubErrorLine(`${source}: ${message}`);
  if (!line || RING[RING.length - 1] === line) return;
  RING.push(line);
  if (RING.length > RING_MAX) RING.shift();
}
/** The recent errors on this page, oldest first (a copy). */
export function recentErrors() { return RING.slice(); }
// From the moment the app loads, before sign-in starts telemetry proper.
try {
  window.addEventListener('error', (e) => { if (!initialised) remember('error', e.message); });
  window.addEventListener('unhandledrejection', (e) => { if (!initialised) { const r = e.reason || {}; remember('promise', r.message || String(r)); } });
} catch (e) { /* no window (tests) */ }

export function logError(source, message, stack) {
  remember(source, message);
  const key = `${source}|${message}`.slice(0, 200);
  if (key === lastErrorKey) return; // collapse repeats
  lastErrorKey = key;
  write({ kind: 'error', source, message: String(message || '').slice(0, 500), stack: String(stack || '').slice(0, 2000) });
}

export function initTelemetry(session, build) {
  ctx = { email: session && session.email, roles: (session && session.roles) || [], build };
  initialised = true;

  window.addEventListener('error', (e) => {
    logError('window.onerror', e.message, e.error && e.error.stack);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason || {};
    logError('unhandledrejection', r.message || String(r), r.stack);
  });

  write({ kind: 'event', name: 'session-start', detail: { roles: ctx.roles } });
}

// Every "Report an issue" now opens the Issue capture (23/10/2026): a
// screenshot to mark up, Bug or Idea, the screen, build, device and recent
// errors, to the company's Owner and Track support. The older dialog below
// stays only as the fallback if that module fails to load.
export function openReportDialog() {
  import('./issue-capture.js').then((m) => m.openIssue()).catch(() => openLegacyReportDialog());
}
function openLegacyReportDialog() {
  const wrap = document.createElement('div');
  wrap.className = 'report-issue-overlay';
  const canShot = !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia);
  wrap.innerHTML = `
    <div class="report-issue-card">
      <h3>Report an issue</h3>
      <p>What went wrong, or what would make this better? We record which screen you're on, your app version and browser.</p>
      <textarea id="ri-note" rows="4" placeholder="Describe it…"></textarea>
      ${canShot ? `<div class="ri-shot">
        <button class="btn ghost" id="ri-shot-btn" type="button">Attach screenshot</button>
        <span id="ri-shot-status" class="ri-shot-status"></span>
      </div>` : ''}
      <div class="report-issue-foot">
        <button class="btn ghost" id="ri-cancel" type="button">Cancel</button>
        <button class="btn primary" id="ri-send" type="button">Send</button>
      </div>
    </div>`;
  document.body.appendChild(wrap);
  const close = () => wrap.remove();
  wrap.addEventListener('click', (e) => { if (e.target === wrap) close(); });
  wrap.querySelector('#ri-cancel').addEventListener('click', close);
  const ta = wrap.querySelector('#ri-note');
  ta.focus();

  let shot = null; // downscaled JPEG data URL, attached to the report if captured
  const shotBtn = wrap.querySelector('#ri-shot-btn');
  const shotStatus = wrap.querySelector('#ri-shot-status');
  if (shotBtn) shotBtn.addEventListener('click', async () => {
    shotBtn.disabled = true; shotStatus.textContent = 'Choose this tab in the prompt…';
    // Hide the report dialog while capturing so it isn't in the screenshot
    // (Karan 14/07: "why does the grab include this window?").
    wrap.style.visibility = 'hidden';
    try {
      shot = await captureScreenshot();
    } catch (e) {
      shot = null;
    } finally {
      wrap.style.visibility = '';
      shotBtn.disabled = false;
    }
    shotStatus.textContent = shot ? 'Screenshot attached' : 'No screenshot captured';
    shotBtn.textContent = shot ? 'Retake' : 'Attach screenshot';
  });

  wrap.querySelector('#ri-send').addEventListener('click', () => {
    const note = ta.value.trim();
    if (note || shot) {
      const entry = { kind: 'issue', message: note.slice(0, 1000) };
      if (shot) entry.shot = shot; // omit entirely when absent (Firestore rejects undefined)
      write(entry);
    }
    const btn = wrap.querySelector('#ri-send');
    btn.textContent = 'Thanks!'; btn.disabled = true;
    setTimeout(close, 700);
  });
}
